import React from 'react';
import { observer } from 'mobx-react-lite';
import { Cross1Icon, PlusIcon } from '@radix-ui/react-icons';
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  MouseSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { IconButton } from '@producer/ui-kit';

import styles from './properties-panel.module.scss';

import { CreatePropertyModal } from './create-property-modal';
import { openModal } from '../../../core/modal/open-modal';
import { useCurrentProject } from '../../../hooks/use-current-project';
import { Property } from '../../../app/entities/property';
import { uiStore } from '../../../core/stores/ui-store';
import { SortablePropertyItem } from './sortable-property-item';
import { View } from '../../../app/entities/view';
import { PinnedPropertyItem } from './pinned-property-item';

interface ListPropertiesPanelProps {
  view: View;
  onEditProperty: (property: Property) => void;
}

export const ListPropertiesPanel: React.FC<ListPropertiesPanelProps> = observer(
  ({ onEditProperty, view }: ListPropertiesPanelProps) => {
    const project = useCurrentProject();

    const sensors = useSensors(
      useSensor(MouseSensor, {
        activationConstraint: {
          distance: 8,
        },
      }),
    );

    const openCreateModal = () =>
      openModal(CreatePropertyModal, {
        title: 'Create custom property',
        project,
      });

    const handlePropertyDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = project.properties.findIndex((p) => p._id === active.id);
        const newIndex = project.properties.findIndex((p) => p._id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          // Here you would typically call an API to update the property order
          // For now, we'll just log the reorder action
          console.log(`Moving property from index ${oldIndex} to ${newIndex}`);
          // TODO: Implement property reordering API call
        }
      }
    };

    const handleToggleProperty = (propertyId: string, value: boolean) => {
      const property = project.properties.find((p) => p._id === propertyId);
      if (!property) return;

      view.update({
        properties: view.properties.map((p) =>
          p.propertyId === propertyId ? { ...p, isVisible: value } : p,
        ),
      });
    };

    return (
      <div className={styles.Container}>
        <div className={styles.PanelHeader}>
          <div className={styles.PanelHeaderTitle}>
            <span>Customize properties</span>
          </div>

          <IconButton
            size={'small'}
            icon={<Cross1Icon />}
            onClick={() => uiStore.closePropertiesPanel()}
          />
        </div>

        {project.properties.length === 0 ? (
          <div style={{ flex: 1 }}>
            <div className={styles.EmptyState}>
              <p>No properties added yet.</p>
            </div>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handlePropertyDragEnd}
          >
            <SortableContext
              items={project.properties.map((property) => property._id)}
              strategy={verticalListSortingStrategy}
            >
              <div className={styles.list}>
                <PinnedPropertyItem
                  value={view?.options?.showBanner}
                  label="Show banners"
                  onToggle={(value: boolean) => {
                    view.update({
                      options: {
                        showBanner: value,
                      },
                    });
                  }}
                />

                {view.allProperties.map((property) => (
                  <SortablePropertyItem
                    key={property._id}
                    property={property}
                    isVisible={property.isVisible ?? true}
                    onClick={onEditProperty}
                    onToggle={handleToggleProperty}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        <div className={styles.PanelFooter}>
          <div className={styles.MenuItem} onClick={openCreateModal}>
            <PlusIcon />
            <span>Create property</span>
          </div>
        </div>
      </div>
    );
  },
);
